import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedCallback } from '../../../core/hooks/useDebouncedCallback';
import { GlobalContext } from '../../../GlobalContext';
import FiltersBar from './components/FiltersBar';
import TagsBar from './components/TagsBar';
import './NewsBrowseSection.styles.scss';
import { NewsTableFilters } from '../NewsTable/useNewsTableFilters';
import { iTag } from '../../../core/models/Tag';
import { BreakpointsEnum, useMediaQuery } from '../../../core/hooks/useMediaQuery';

interface Props {
    filters: NewsTableFilters;
    tags: iTag[];
    onChange: (next: Props['filters']) => void;
}

const NewsBrowseSection: React.FC<Props> = ({ filters, onChange, tags }) => {
    const { getRem } = useContext(GlobalContext);
    const [inputSearchQuery, setInputSearchQuery] = useState(filters.searchQuery ?? '');
    const searchQueryInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useMediaQuery(BreakpointsEnum.SM);

    const shouldRetainInputFocusRef = useRef(false);
    const isUsersInputRef = useRef(false);

    const debouncedSearchChange = useDebouncedCallback(
        (value: string) => {
            if (filters.searchQuery !== value) {
                onChange({ ...filters, searchQuery: value });
            }
        },
        500,
        []
    );

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            isUsersInputRef.current = true;
            shouldRetainInputFocusRef.current = true;
            setInputSearchQuery(value);
            debouncedSearchChange(value);
        },
        [debouncedSearchChange]
    );

    const handleSearchClear = useCallback(() => {
        isUsersInputRef.current = true;
        shouldRetainInputFocusRef.current = true;
        setInputSearchQuery('');
        onChange({ ...filters, searchQuery: '' });
    }, [filters, onChange]);

    const handleSearchBlur = useCallback(() => {
        shouldRetainInputFocusRef.current = false;
    }, []);

    useEffect(() => {
        if (shouldRetainInputFocusRef.current && searchQueryInputRef.current) {
            searchQueryInputRef.current.focus();
        }
    });

    useEffect(() => {
        if (!isUsersInputRef.current && filters.searchQuery !== inputSearchQuery) {
            setInputSearchQuery(filters.searchQuery ?? '');
        }
        isUsersInputRef.current = false;
    }, [filters.searchQuery, inputSearchQuery]);

    const isTagsDropdownDisabled = tags?.length === 0;
    const hasFilters = !!filters.tagsIds?.length || !!filters.searchQuery;

    const tagsOptions = useMemo(() => {
        if (isTagsDropdownDisabled) {
            return [];
        }

        const tagsMap = new Map<number, iTag>();
        [...tags].forEach((opt) => {
            if (!tagsMap.has(opt.id)) tagsMap.set(opt.id, opt);
        });

        return Array.from(tagsMap.values()).sort((a, b) => {
            const aTitle = a.title.toLowerCase();
            const bTitle = b.title.toLowerCase();
            if (aTitle < bTitle) return -1;
            if (aTitle > bTitle) return 1;
            return 0;
        });
    }, [tags, isTagsDropdownDisabled]);

    const selectedTags: iTag[] = useMemo(() => {
        return filters?.tagsIds?.map((id) => tags?.find((t) => t?.id === id))?.filter((t): t is iTag => t !== undefined) ?? [];
    }, [filters?.tagsIds, tags]);

    const handleRemoveTag = useCallback(
        (id: number) => {
            onChange({ ...filters, tagsIds: filters?.tagsIds?.filter((t) => t !== id) ?? [] });
        },
        [filters, onChange]
    );

    const handleSortChange = useCallback(
        (orderBy: NewsTableFilters['orderBy'], sortOrder: NewsTableFilters['sortOrder']) => {
            onChange({ ...filters, orderBy, sortOrder });
        },
        [filters, onChange]
    );

    return (
        <div className="NewsBrowseSection">
            <div className="NewsBrowseSection__main">
                <FiltersBar
                    tagsOptions={tagsOptions}
                    filters={filters}
                    selectedTags={selectedTags}
                    onChange={(next) => onChange({ ...filters, ...next })}
                    onSearchChange={handleSearchChange}
                    localSearchQuery={inputSearchQuery}
                    searchInputRef={searchQueryInputRef}
                    onSearchClear={handleSearchClear}
                    onSearchBlur={handleSearchBlur}
                    getRem={getRem}
                    hasFilters={hasFilters}
                    isTagsDropdownDisabled={isTagsDropdownDisabled}
                    isMobile={isMobile}
                    onSortChange={handleSortChange}
                />

                <TagsBar selectedTags={selectedTags} handleRemoveTag={handleRemoveTag} />
            </div>
        </div>
    );
};

export default NewsBrowseSection;
